import React, { useState, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { ProductImage } from '../../types/product'

interface ImagePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  images: ProductImage[]
  currentIndex: number
  onImageChange?: (index: number) => void
}

const IMAGE_LABELS: Record<string, string> = {
  front: 'Face avant',
  back: 'Ingrédients (1)',
  detailed: 'Ingrédients (2)',
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ 
  isOpen, 
  onClose, 
  images,
  currentIndex,
  onImageChange
}) => {
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex)
  const containerRef = useRef<HTMLDivElement>(null)

  const MIN_ZOOM = 0.5
  const MAX_ZOOM = 4
  const ZOOM_STEP = 0.25

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM))
  }

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1
      setCurrentImageIndex(newIndex)
      onImageChange?.(newIndex)
      setZoom(1)
      setPosition({ x: 0, y: 0 })
    }
  }

  const handleNextImage = () => {
    if (currentImageIndex < images.length - 1) {
      const newIndex = currentImageIndex + 1
      setCurrentImageIndex(newIndex)
      onImageChange?.(newIndex)
      setZoom(1)
      setPosition({ x: 0, y: 0 })
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || zoom <= 1) return
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Reset zoom when modal opens/closes or image changes
  React.useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(currentIndex)
      setZoom(1)
      setPosition({ x: 0, y: 0 })
    }
  }, [isOpen, currentIndex])

  // Handle wheel zoom with non-passive listener
  React.useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) return
      
      e.preventDefault()
      
      if (e.deltaY < 0) {
        handleZoomIn()
      } else {
        handleZoomOut()
      }
    }

    if (isOpen && containerRef.current) {
      // Add non-passive wheel event listener
      const container = containerRef.current
      container.addEventListener('wheel', handleWheel, { passive: false })
      
      return () => {
        container.removeEventListener('wheel', handleWheel)
      }
    }
  }, [isOpen, zoom])

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          handlePrevImage()
          break
        case 'ArrowRight':
          e.preventDefault()
          handleNextImage()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, currentImageIndex, images.length])

  if (!images || images.length === 0) return null

  const currentImage = images[currentImageIndex]
  const imageUrl = currentImage?.imageUrl || currentImage?.image
  const imageType = currentImage?.imageType || 'front'
  const title = IMAGE_LABELS[imageType] || 'Image'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      className="!max-w-[95vw] !max-h-[95vh]"
      showCloseButton={false}
      closeOnOverlayClick={true}
    >
      <div className="flex flex-col h-full">
        {/* Header bar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Image container with navigation arrows */}
        <div className="flex-1 relative overflow-hidden bg-gray-50">
          {/* Previous arrow */}
          {currentImageIndex > 0 && (
            <button
              onClick={handlePrevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
          )}

          {/* Next arrow */}
          {currentImageIndex < images.length - 1 && (
            <button
              onClick={handleNextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
              aria-label="Next image"
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          )}

          {/* Image viewer */}
          <div
            ref={containerRef}
            className="w-full h-full"
            style={{ 
              cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              minHeight: '600px'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose()
            }}
            role="button"
            tabIndex={0}
            aria-label="Draggable and zoomable image viewer - use mouse to drag when zoomed, press Escape to close"
          >
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
            >
              <img
                src={imageUrl}
                alt={title}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 text-sm text-gray-500 text-center border-t border-gray-200">
          Utilisez la molette de la souris pour zoomer • Cliquez et glissez pour déplacer l&apos;image (en mode zoom) • Flèches ← → pour naviguer
        </div>
      </div>
    </Modal>
  )
}